import { AccountCentralizedInfo, AccountInfoResult } from './AccountInfo';
import { CLTypeParsedResult, CLTypeTypeResult } from './CLType';
import { DataResponse } from './Validator';
import { Approval } from './Multisig';

export type Response<T = any> = {
  data: T[];
};

export type PaginatedResponse<T = any> = {
  data: T[];
  item_count: number;
  page_count: number;
};

export type DeployEntryPointResult = {
  action_type_id: null;
  contract_hash: string | null;
  contract_package_hash: string | null;
  id: string | null;
  name: string | null;
};

export type DeployContractPackageMetadata = {
  symbol: string;
  name: string;
  decimals: number;
  balances_uref: string;
  total_supply_uref: string;
  burn_mode?: string;
  holder_mode?: string;
  identifier_mode?: string;
  metadata_mutability?: string;
  minting_mode?: string;
  nft_kind?: string;
  nft_metadata_kind?: string;
  ownership_mode?: string;
  whitelist_mode?: string;
  owner_reverse_lookup_mode?: string;
};

export type DeployContractPackageResult = {
  description: string | null;
  icon_url: string | null;
  name: string | null;
  contract_package_hash: string;
  latest_version_contract_type_id: number | null;
  owner_public_key: string | null;
  owner_hash: string | null;
  timestamp: string;
  has_ces_events: boolean;
  deploys_number?: number;
  activity_num?: number;
  metadata?: DeployContractPackageMetadata;
  account_info?: AccountInfoResult | null;
  centralized_account_info?: AccountCentralizedInfo | null;
  contract_description?: string;
  cspr_name?: string | null;
  // TODO: remove these types
  contract_type_id?: number | null;
  contract_name: string | null;
  website_url?: string | null;
};

export type DeployClTypeResult = {
  cl_type: CLTypeTypeResult;
  parsed: CLTypeParsedResult | string | null;
};

export type DeployArgsResult = {
  amount?: DeployClTypeResult;
  spender?: DeployClTypeResult;
  bsc_recipient_address?: DeployClTypeResult;
  contract_hash_str?: DeployClTypeResult;
  recipient?: DeployClTypeResult;
  owner?: DeployClTypeResult;
  token_id?: DeployClTypeResult;
  token_meta?: DeployClTypeResult;
  token_metas?: DeployClTypeResult;
  token_meta_data?: DeployClTypeResult;
  id?: DeployClTypeResult;
  target?: DeployClTypeResult;
  contract_name?: DeployClTypeResult;
  decimals?: DeployClTypeResult;
  initial_supply?: DeployClTypeResult;
  name?: DeployClTypeResult;
  symbol?: DeployClTypeResult;
  amount_in?: DeployClTypeResult;
  amount_out_min?: DeployClTypeResult;
  deadline?: DeployClTypeResult;
  path?: DeployClTypeResult;
  to?: DeployClTypeResult;
  tokens?: DeployClTypeResult;
  delegator?: DeployClTypeResult;
  validator?: DeployClTypeResult;
  new_validator?: DeployClTypeResult;
  offerer?: DeployClTypeResult;
  collection?: DeployClTypeResult;
  token_owner?: DeployClTypeResult;
  operator?: DeployClTypeResult;
  token_ids?: DeployClTypeResult;
  target_key?: DeployClTypeResult;
  source_key?: DeployClTypeResult;
};

export type NftActionsResult = {
  block_height: number;
  contract_package: DeployContractPackageResult;
  contract_package_hash: string;
  deploy_hash: string;
  from_hash: null;
  from_public_key: string | null;
  from_type: null;
  nft_action_id: number;
  timestamp: string;
  to_hash: string;
  to_public_key: string | null;
  to_type: number;
  token_id: string;
  token_tracking_id: number;
  transform_idx: number;
};

export enum HashType {
  accountHash = 'account-hash',
  hash = 'hash',
}

export enum TransactorHashType {
  'account' = 0,
  'hash' = 1,
}

export type FTActionsResult = {
  amount: string;
  contract_package: DeployContractPackageResult;
  contract_package_hash: string;
  deploy_hash: string;
  ft_action_type_id: number;
  from_hash: string;
  from_public_key: string | null;
  from_type: TransactorHashType;
  timestamp: string;
  to_hash: string;
  to_public_key: string | null;
  to_type: TransactorHashType;
  transform_idx: number;
};

export type DeployTransferResult = {
  amount: string;
  block_height: number;
  transfer_index: number;
  deploy_hash: string;
  from_purse_public_key: string | null;
  from_purse: string;
  id: string | null;
  initiator_account_hash: string;
  timestamp: string;
  to_account_hash: string;
  to_purse: string;
  transform_key: string;
  to_purse_public_key: string | null;
};

export interface DeployResult {
  // means it's a pending deploy
  args: DeployArgsResult;
  block_hash: string | null;
  caller_public_key: string;
  caller_hash: string;
  contract_entrypoint?: DeployEntryPointResult;
  contract_hash: string | null;
  contract_package: DeployContractPackageResult;
  contract_package_hash: string | null;
  cost: string;
  consumed_gas: string;
  deploy_hash: string;
  error_message: string | null;
  execution_type_id: number;
  nft_token_actions?: NftActionsResult[];
  ft_token_actions?: FTActionsResult[];
  payment_amount: string | null;
  refund_amount?: string | null;
  rate: number;
  status: string;
  timestamp: string;
  transfers?: DeployTransferResult[];
  pending?: boolean;
  account_info?: AccountInfoResult;
  centralized_account_info?: AccountCentralizedInfo;
  cspr_name?: string;
  amount: string | null;
  currency_cost: number;
  caller_cspr_name?: string | null;
  block_height: number;
}

export interface Deploy {
  // means it's a pending deploy
  accountInfo?: AccountInfoResult;
  amount: string | null;
  args: DeployArgsResult;
  blockHash: string | null;
  blockHeight: string | null;
  callerCsprName?: string | null;
  callerHash: string;
  callerPublicKey: string;
  centralizedAccountInfo?: AccountCentralizedInfo | null;
  consumedGas: string;
  contractHash: string | null;
  contractPackage: DeployContractPackageResult;
  contractPackageHash: string | null;
  cost: string;
  deployHash: string;
  entryPoint?: DeployEntryPointResult;
  errorMessage: string | null;
  executionTypeId: number;
  ftActions?: FTActionsResult[];
  nftActions?: NftActionsResult[];
  paymentAmount: string | null;
  pending?: boolean;
  refundAmount?: string | null;
  status: string;
  timeTransactionCurrencyRate: number;
  timestamp: string;
  transfers?: DeployTransferResult[];
}

export const MapPaginatedDeploy = ({
  data,
  ...rest
}: DataResponse<DeployResult>): Deploy =>
  <Deploy>{
    ...rest,
    ...MapDeploy(data),
  };

export const MapDeploy = ({
  deploy_hash,
  block_hash,
  caller_public_key,
  contract_hash,
  contract_package_hash,
  error_message,
  payment_amount,
  contract_entrypoint,
  contract_package,
  execution_type_id,
  nft_token_actions,
  ft_token_actions,
  account_info,
  centralized_account_info,
  rate,
  caller_cspr_name,
  caller_hash,
  consumed_gas,
  refund_amount,
  block_height,
  ...rest
}: DeployResult): Deploy => ({
  ...rest,
  accountInfo: account_info,
  blockHash: block_hash,
  blockHeight: block_height,
  callerCsprName: caller_cspr_name,
  callerHash: caller_hash,
  callerPublicKey: caller_public_key,
  centralizedAccountInfo: centralized_account_info,
  consumedGas: consumed_gas,
  contractHash: contract_hash,
  contractPackage: contract_package,
  contractPackageHash: contract_package_hash,
  deployHash: deploy_hash,
  entryPoint: contract_entrypoint,
  errorMessage: error_message,
  executionTypeId: execution_type_id,
  ftActions: ft_token_actions,
  nftActions: nft_token_actions,
  paymentAmount: payment_amount,
  refundAmount: refund_amount,
  timeTransactionCurrencyRate: rate,
});

export const MapPaginatedDeploys = ({
  data,
  ...rest
}: PaginatedResponse<DeployResult>): PaginatedResponse<Deploy> => ({
  ...rest,
  data: data ? data.map(MapDeploy) : [],
});

export interface DeployResultWithInfo extends Deploy {
  accountInfo?: AccountInfoResult;
}

export function Deploy(obj: any): DeployResult {
  return obj;
}

export enum DeployEntryPointName {
  TRANSFER = 'transfer',
}

export interface ParsedItem {
  key: string;
  value: string;
}

export interface RawDeployJSON {
  session: Record<string, any>;
  approvals: Approval[];
  header: {
    account: string;
    timestamp: number;
    ttl: number;
    gas_price: number;
    body_hash: string;
    dependencies: [];
    chain_name: string;
  };
  payment: Record<string, any>;
  hash: string;
}
